import type { PhotoDetail } from '@/types/photo';
import { API_BASE_URL } from '@/services/api';

export class PhotoApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PhotoApiError';
    this.status = status;
  }
}

interface PhotoListResponse {
  photos: PhotoDetail[];
}

interface MemoryPhotoListResponse extends PhotoListResponse {
  title: string;
  memoryDate: string;
}

interface PhotoResponse {
  photo: PhotoDetail;
  message?: string;
}

export interface DuplicateUploadInfo {
  id: string;
  albumId: string;
  albumTitle?: string;
  originalFilename: string;
  reason: 'same-file' | 'same-photo' | string;
}

export interface PhotoUploadResult {
  photos: PhotoDetail[];
  message?: string;
  albumIds: string[];
  duplicateCount: number;
  duplicates: DuplicateUploadInfo[];
}

export interface PhotoUploadProgress {
  loaded: number;
  total: number;
  percent: number;
  phase: 'uploading' | 'processing';
}

export type PhotoUploadProgressHandler = (progress: PhotoUploadProgress) => void;

interface PhotoUploadResponse {
  photos: PhotoDetail[];
  message?: string;
  albumIds?: string[];
  duplicateCount?: number;
  duplicates?: DuplicateUploadInfo[];
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { detail?: string; message?: string; error?: string };
    return body.detail || body.message || body.error || '서버 오류가 발생했습니다';
  } catch {
    return '서버 오류가 발생했습니다';
  }
}

async function authFetch(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = 30000,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const message = await readErrorMessage(response);
      throw new PhotoApiError(message, response.status);
    }

    return response;
  } catch (error) {
    if (error instanceof PhotoApiError) throw error;
    if ((error as Error).name === 'AbortError') {
      throw new PhotoApiError('요청 시간이 초과되었습니다', 408);
    }
    throw new PhotoApiError('네트워크 연결을 확인해주세요', 0);
  } finally {
    window.clearTimeout(timeout);
  }
}

function readXhrErrorMessage(xhr: XMLHttpRequest): string {
  try {
    const body = JSON.parse(xhr.responseText || '{}') as { detail?: string; message?: string; error?: string };
    return body.detail || body.message || body.error || `업로드에 실패했습니다. (${xhr.status})`;
  } catch {
    return xhr.status ? `업로드에 실패했습니다. (${xhr.status})` : '네트워크 연결을 확인해주세요';
  }
}

function uploadFormData(
  endpoint: string,
  formData: FormData,
  expectedTotal: number,
  onProgress?: PhotoUploadProgressHandler,
  timeoutMs: number = 15 * 60 * 1000,
): Promise<PhotoUploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const totalHint = Math.max(1, expectedTotal);
    let lastLoaded = 0;
    let lastTotal = totalHint;
    let settled = false;

    const report = (
      loaded: number,
      total: number,
      phase: PhotoUploadProgress['phase'],
    ) => {
      const safeTotal = Math.max(1, total || totalHint);
      const safeLoaded = Math.max(0, Math.min(loaded, safeTotal));
      const percent = phase === 'processing'
        ? 100
        : Math.min(99, Math.max(0, Math.round((safeLoaded / safeTotal) * 100)));
      onProgress?.({ loaded: safeLoaded, total: safeTotal, percent, phase });
    };

    const rejectOnce = (error: PhotoApiError) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    xhr.open('POST', `${API_BASE_URL}${endpoint}`, true);
    xhr.withCredentials = true;
    xhr.timeout = timeoutMs;

    // PC Chrome/Edge, Android Chrome, iPhone Safari에서 동일하게 0% 화면을 먼저 표시합니다.
    report(0, totalHint, 'uploading');

    xhr.upload.onloadstart = () => report(0, totalHint, 'uploading');
    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : totalHint;
      const loaded = event.loaded;
      lastLoaded = Math.max(lastLoaded, loaded);
      lastTotal = Math.max(1, total);
      report(lastLoaded, lastTotal, 'uploading');
    };

    // 파일 전송이 끝나도 서버의 중복 검사·촬영일 분류·썸네일 작업은 계속됩니다.
    xhr.upload.onload = () => {
      const total = Math.max(lastTotal, lastLoaded, totalHint);
      report(total, total, 'processing');
    };

    xhr.onload = () => {
      if (settled) return;
      if (xhr.status < 200 || xhr.status >= 300) {
        rejectOnce(new PhotoApiError(readXhrErrorMessage(xhr), xhr.status));
        return;
      }
      try {
        const data = JSON.parse(xhr.responseText) as PhotoUploadResponse;
        settled = true;
        resolve(data);
      } catch {
        rejectOnce(new PhotoApiError('업로드 결과를 확인할 수 없습니다', 500));
      }
    };

    xhr.onerror = () => rejectOnce(new PhotoApiError('네트워크 연결을 확인해주세요', 0));
    xhr.ontimeout = () => rejectOnce(new PhotoApiError('업로드 시간이 초과되었습니다', 408));
    xhr.onabort = () => rejectOnce(new PhotoApiError('업로드가 취소되었습니다', 499));
    xhr.send(formData);
  });
}

function parseFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) return fallback;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const normalMatch = disposition.match(/filename="?([^";]+)"?/i);
  return normalMatch?.[1] || fallback;
}

export async function getPhotoById(id: string): Promise<PhotoDetail | undefined> {
  try {
    const response = await authFetch(`/api/photos/${encodeURIComponent(id)}`);
    const data = await response.json() as PhotoResponse;
    return data.photo;
  } catch (error) {
    if (error instanceof PhotoApiError && error.status === 404) return undefined;
    throw error;
  }
}

export async function getPhotosByAlbumId(albumId: string): Promise<PhotoDetail[]> {
  const response = await authFetch(`/api/albums/${encodeURIComponent(albumId)}/photos`);
  const data = await response.json() as PhotoListResponse;
  return data.photos;
}

export async function getRecentPhotos(count: number): Promise<PhotoDetail[]> {
  const limit = Math.max(1, Math.min(count, 50));
  const response = await authFetch(`/api/photos/recent?limit=${limit}`);
  const data = await response.json() as PhotoListResponse;
  return data.photos;
}

export interface PhotoSearchOptions {
  query?: string;
  albumId?: string;
  shotDate?: string;
  favoriteOnly?: boolean;
  limit?: number;
}

export async function searchPhotos(options: PhotoSearchOptions = {}): Promise<PhotoDetail[]> {
  const params = new URLSearchParams();
  if (options.query?.trim()) params.set('q', options.query.trim());
  if (options.albumId) params.set('albumId', options.albumId);
  if (options.shotDate) params.set('shotDate', options.shotDate.replaceAll('.', '-'));
  if (options.favoriteOnly) params.set('favoriteOnly', 'true');
  if (options.limit) params.set('limit', String(options.limit));

  const suffix = params.toString();
  const response = await authFetch(`/api/photos/search${suffix ? `?${suffix}` : ''}`);
  const data = await response.json() as PhotoListResponse;
  return data.photos;
}

export async function getFavoritePhotos(): Promise<PhotoDetail[]> {
  const response = await authFetch('/api/photos/favorites');
  const data = await response.json() as PhotoListResponse;
  return data.photos;
}

export async function getLastYearTodayMemories(): Promise<{
  title: string;
  memoryDate: string;
  photos: PhotoDetail[];
}> {
  const response = await authFetch('/api/photos/memories/last-year-today');
  const data = await response.json() as MemoryPhotoListResponse;
  return data;
}

export async function setPhotoFavorite(
  id: string,
  favorite: boolean,
): Promise<PhotoDetail | undefined> {
  try {
    const response = await authFetch(`/api/photos/${encodeURIComponent(id)}/favorite`, {
      method: favorite ? 'POST' : 'DELETE',
    });
    const data = await response.json() as PhotoResponse;
    return data.photo;
  } catch (error) {
    if (error instanceof PhotoApiError && error.status === 404) return undefined;
    throw error;
  }
}

export async function uploadPhoto(
  albumId: string,
  file: File,
  caption: string,
  shotDate?: string,
): Promise<PhotoDetail> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('clientModifiedDate', new Date(file.lastModified || Date.now()).toISOString());
  formData.append('caption', caption);
  if (shotDate) formData.append('shotDate', shotDate.replaceAll('.', '-'));

  const response = await authFetch(
    `/api/albums/${encodeURIComponent(albumId)}/photos/single`,
    {
      method: 'POST',
      body: formData,
    },
    15 * 60 * 1000,
  );

  const data = await response.json() as PhotoUploadResponse;
  const uploaded = data.photos[0];
  if (!uploaded) throw new PhotoApiError('업로드 결과를 확인할 수 없습니다', 500);
  return uploaded;
}

export async function uploadPhotosToAlbum(
  albumId: string,
  files: File[],
  onProgress?: PhotoUploadProgressHandler,
): Promise<PhotoUploadResult> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file, file.name);
    formData.append('clientModifiedDates', new Date(file.lastModified || Date.now()).toISOString());
  });

  const expectedTotal = files.reduce((sum, file) => sum + file.size, 0);
  const data = await uploadFormData(
    `/api/albums/${encodeURIComponent(albumId)}/photos`,
    formData,
    expectedTotal,
    onProgress,
  );
  return {
    photos: data.photos,
    albumIds: data.albumIds ?? [albumId],
    message: data.message,
    duplicateCount: data.duplicateCount ?? 0,
    duplicates: data.duplicates ?? [],
  };
}

export async function autoOrganizePhotos(
  files: File[],
  onProgress?: PhotoUploadProgressHandler,
): Promise<PhotoUploadResult> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file, file.name);
    formData.append('clientModifiedDates', new Date(file.lastModified || Date.now()).toISOString());
  });

  const expectedTotal = files.reduce((sum, file) => sum + file.size, 0);
  const data = await uploadFormData(
    '/api/photos/auto-organize',
    formData,
    expectedTotal,
    onProgress,
  );
  return {
    photos: data.photos,
    albumIds: data.albumIds ?? [],
    message: data.message,
    duplicateCount: data.duplicateCount ?? 0,
    duplicates: data.duplicates ?? [],
  };
}

export async function downloadPhoto(id: string): Promise<{ blob: Blob; filename: string }> {
  const response = await authFetch(`/api/photos/${encodeURIComponent(id)}/download`, {}, 120000);
  return {
    blob: await response.blob(),
    filename: parseFilename(response, `photo_${id}.jpg`),
  };
}

export async function downloadPhotosAsZip(
  photoIds: string[],
): Promise<{ blob: Blob; filename: string }> {
  const response = await authFetch('/api/photos/download-zip', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ photoIds }),
  }, 180000);

  return {
    blob: await response.blob(),
    filename: parseFilename(response, 'family_photos.zip'),
  };
}

export async function updatePhotoCaption(
  id: string,
  caption: string,
): Promise<PhotoDetail | undefined> {
  try {
    const response = await authFetch(`/api/photos/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption }),
    });
    const data = await response.json() as PhotoResponse;
    return data.photo;
  } catch (error) {
    if (error instanceof PhotoApiError && error.status === 404) return undefined;
    throw error;
  }
}

export async function deletePhoto(id: string): Promise<boolean> {
  await authFetch(`/api/photos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return true;
}

export async function deletePhotosBatch(photoIds: string[]): Promise<{
  message: string;
  deletedIds: string[];
  deletedCount: number;
}> {
  const response = await authFetch('/api/photos/delete-batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ photoIds }),
  });
  return response.json() as Promise<{
    message: string;
    deletedIds: string[];
    deletedCount: number;
  }>;
}
